import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Horizontally scrollable filter row: swipeable on touch, with edge arrows that
// only appear when there is something more to scroll to.
export const FilterCarousel = ({ children, testid }) => {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", check);
      ro.disconnect();
    };
  }, [check, children]);

  const nudge = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: "smooth" });
  };

  const Arrow = ({ dir, side }) => (
    <button
      onClick={() => nudge(dir)}
      className={`absolute ${side} top-1/2 z-10 -translate-y-1/2 rounded-full border border-[#e5e0d8] bg-white/95 p-1.5 text-[#2d2824] shadow-md backdrop-blur transition-transform active:scale-90`}
      data-testid={`${testid}-${dir < 0 ? "prev" : "next"}`}
      aria-label={dir < 0 ? "scroll left" : "scroll right"}
    >
      {dir < 0 ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  );

  return (
    <div className="relative" data-testid={testid}>
      {edges.left && <Arrow dir={-1} side="left-0" />}
      {edges.right && <Arrow dir={1} side="right-0" />}
      <div
        ref={ref}
        data-carousel-track
        className="flex items-center gap-2 overflow-x-auto scroll-smooth px-1 py-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
    </div>
  );
};
